<script>
import Vue from "vue";
//import store from "@/store";
import InputField from "@/components/InputField.vue";
import { arrowDownFilled, arrowDownIcon, arrowUpFilled, crossIcon, generateIcon, loadingSmIcon, projectIcon, refreshIcon, uploadPhotoIcon, userIcon } from "@/utils/proposalIcons";

export default {
  name: "InfoCard",
  components: {
    InputField
  },
  props: {
    list: { type: Array, required: true },
    idKey: { type: String, default: "projectId" },
    labelKey: { type: String, default: "projectName" },
    isAI: { type: Boolean, default: false },
    textLimit: { type: Number, default: Infinity },
    context: { type: String, default: "" },
  },
  data() {
    return {
      errorMessage: "",
      openAiMap: {},
      valueMap: {},
      // icons
      arrowDownFilled,
      arrowDownIcon,
      arrowUpFilled,
      crossIcon,
      generateIcon,
      loadingSmIcon,
      projectIcon,
      refreshIcon,
      uploadPhotoIcon,
      userIcon
    };
  },
  watch: {
    list: {
      handler() {
        this.initializeValues();
      },
      deep: true
    }
  },
  methods: {
    toggleOpenAi(id) {
      const isCurrentlyOpen = this.openAiMap[id];
      this.openAiMap = {};
      if (!isCurrentlyOpen) {
        Vue.set(this.openAiMap, id, true);
      }
    },
    getType() {
      const baseType = this.idKey === "teamMemberId" ? "member" : "project";
      return this.context ? `${this.context}-${baseType}` : baseType;
    },
    isLoading(id) {
      const type = this.getType();
      const key = `${type}-${id}`;
      return this.$store.state.loading.aiGenerateById[key] || false;
    },
    getCachedResponse(id) {
      const type = this.getType();
      const key = `${type}-${id}`;
      return this.$store.state.aiResponses[key] || "";
    },
    initializeValues() {
      const newMap = {};
      this.list.forEach(item => {
        const id = item[this.idKey];
        let currentValue = "";
        if (this.idKey === "teamMemberId") {
          currentValue = item.whyTeamMember || "";
        } else if (this.idKey === "projectId") {
          currentValue = item.projectDetails || "";
        }
        
        if (this.isAI && (!currentValue || (this.idKey === "projectId" && currentValue.length > this.textLimit))) {
          const cached = this.getCachedResponse(id);
          if (cached && (this.idKey !== "projectId" || cached.length <= this.textLimit)) {
            currentValue = cached;
            if (this.idKey === "teamMemberId") {
              Vue.set(item, 'whyTeamMember', cached);
            } else if (this.idKey === "projectId") {
              Vue.set(item, 'projectDetails', currentValue);
            }
          }
        }
        newMap[id] = currentValue;
      });
      this.valueMap = newMap;
    },
    handleRemove(id) {
      this.$emit("remove-item", id);
    },
    moveUp(index) {
      if (index === 0) return;
      this.$emit("move-item", { from: index, to: index - 1 });
    },
    moveDown(index) {
      if (index === this.list.length - 1) return;
      this.$emit("move-item", { from: index, to: index + 1 });
    },
    handleInput(item, value) {
      const id = item[this.idKey];
      Vue.set(this.valueMap, id, value);
      if (this.idKey === "teamMemberId") {
        Vue.set(item, 'whyTeamMember', value);
      } else if (this.idKey === "projectId") {
        Vue.set(item, 'projectDetails', value);
      }
      const type = this.getType();
      this.$store.commit('SET_AI_RESPONSE_BY_ID', { type, id, response: value });
      this.$emit("update", [...this.list]);
    },
    async handleGenerateResponse(item) {
      const id = item[this.idKey];
      const type = this.getType();
      
      let prompt = "";
      if (this.idKey === "teamMemberId") {
        prompt = `Write a concise introduction section for a resume highlighting his role. The candidate’s name is ${item[this.labelKey]} and his role is ${item.role}. In 1–2 professional sentences (maximum ${this.textLimit} characters), explain why an employer should select him by summarizing his primary responsibilities, relevant experience, and key skills. Maintain a factual, objective tone similar to a job description or professional summary. Avoid marketing language, buzzwords, first-person phrasing, headings, or exaggerated claims.`;
      } else if (this.idKey === "projectId") {
        const currentDetails = item.projectDetails || "";
        if (currentDetails.length > this.textLimit) {
          prompt = `Summarize the following project details for the project "${item[this.labelKey]}" to be close to and maximum ${this.textLimit} characters while maintaining a professional, objective tone: ${currentDetails}`;
        } else {
          prompt = `Write a concise project scope description for the project "${item[this.labelKey]}". In 1–2 professional sentences (maximum ${this.textLimit} characters), describe the scope of work the team was responsible for, including key objectives, responsibilities, and outcomes. Keep the tone factual and objective, suitable for a resume or project experience section. Avoid marketing language, promotional wording, headings, or exaggerated claims.`;
        }
      }
      if (prompt) {
        try {
          const response = await this.$store.dispatch("generateAIResponseById", { type, id, prompt });
		  console.log('In handleGenerateResponse');
		  console.log(response);
          if (response) {
            this.handleInput(item, response);
          }
        } catch (error) {
          console.log("AI Generation failed:", error);
          this.errorMessage = "AI response failed. Please try again.";
          setTimeout(() => {
            this.errorMessage = "";
          }, 3000);
        }
      }
    }
  },
  mounted() {
    this.initializeValues();
  }
};
</script>

<template>
  <div class="list-cont">
    <!-- Hidden file input for photo upload -->
    <!-- <input type="file" ref="fileInput" @change="handlePhotoUpload" accept="image/jpeg, image/png" class="d-none" /> -->

    <div v-for="(item, index) in list" :key="item[idKey]" @click="toggleOpenAi(item[idKey])" class="list-item"
      :class="{ 'limit-exceeded': isAI && !isLoading(item[idKey]) && ((textLimit !== Infinity && Math.max(0, (valueMap[item[idKey]] || '').length) > Number(textLimit)) || !(valueMap[item[idKey]] || '').trim()) }"
      :role="isAI && 'button'">
      <!-- Layer buttons -->
      <div class="info-row">
        <div v-if="list.length > 1" class="layers-sec">
          <button class="layers-btn" @click.stop="moveUp(index)" :disabled="index === 0">
            <span v-html="arrowUpFilled" />
          </button>
          <button class="layers-btn" @click.stop="moveDown(index)" :disabled="index === list.length - 1">
            <span v-html="arrowDownFilled" />
          </button>
        </div>

        <!-- Details -->
        <div class="details-sec">
          <span class="icon" v-html="idKey === 'teamMemberId' ? userIcon : projectIcon"></span>
          <span class="name">{{ item[labelKey] }}</span>
          <span v-if="item.title || item.owner" class="detail">
            ({{ item.title || item.owner }})
          </span>
        </div>

        <!-- Custom Photo Actions -->
        <!-- <div v-if="idKey === 'projectId'" class="photo-actions">
          <button v-if="item.uploadedPhoto" class="photo-btn remove" @click.stop="removeUploadedPhoto(item)">
            <span class="icon" v-html="refreshIcon"></span>
          </button>
          <button class="photo-btn upload" @click.stop="triggerPhotoUpload(item)">
            <span class="icon" v-html="uploadPhotoIcon"></span>
          </button>
        </div> -->

        <!-- Generate button -->
        <button v-if="isAI" class="generate-arrow" :class="{ open: openAiMap[item[idKey]] }"><span
            v-html="arrowDownIcon" /></button>

        <!-- Remove button -->
        <span v-if="isAI && isLoading(item[idKey])" v-html="loadingSmIcon" class="loader-icon"
          :class="{ loading: isLoading(item[idKey]) }" />
        <button v-else @click.stop="handleRemove(item[idKey])" class="remove-btn"><span v-html="crossIcon" /></button>
      </div>

      <div v-if="isAI && openAiMap[item[idKey]]" class="d-flex flex-column m-2 mx-2" @click.stop>
        <div class="d-flex align-items-center">
          <div class="d-flex flex-column w-100">
            <InputField :isTextarea="true" :rows="2" :modelValue="item.projectDetails||valueMap[item[idKey]] || ''"
              @update:modelValue="handleInput(item, $event)"
              :placeholder="idKey === 'teamMemberId' ? `Generate response about employee role` : `Generate response for project scope`"
              :disabled="isLoading(item[idKey])" :maxlength="textLimit !== Infinity ? textLimit : null" />
            <div v-if="textLimit !== Infinity" class="char-counter">
              {{ (valueMap[item[idKey]] || '').length }}/{{ textLimit }}
            </div>
          </div>
          <button class="generate-btn" :class="{ loading: isLoading(item[idKey]) }"
            @click.stop="handleGenerateResponse(item)" :disabled="isLoading(item[idKey])">
            <span class="icon" v-html="generateIcon"></span>
          </button>
        </div>

      </div>
    </div>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  </div>
</template>

<style scoped lang="scss">
.list-cont {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .list-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: #f8f8f8;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    width: 100%;
    min-height: 40px;
    box-sizing: border-box;
    padding: 2px;

    &.limit-exceeded {
      border: 1px solid #dc3545 !important;
      background: #fff8f8;
    }

    .info-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      min-height: 34px;

      .layers-sec {
        display: flex;
        flex-direction: column;
        margin-right: 5px;
        flex-shrink: 0;
        margin-left: 5px;

        .layers-btn {
          width: 14px;
          height: 10px;
          font-size: 8px;
          line-height: 1;
          color: #999;
          cursor: pointer;
          background: #f0f0f0;
          border: 1px solid #ddd;
          border-radius: 2px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 1px 0;

          &:hover {
            color: #666;
            border-color: #999;
            background: #e8e8e8;
          }

          &:disabled {
            color: #ccc;
            border-color: #eee;
            background: #f8f8f8;
            cursor: not-allowed;
          }
        }
      }

      .details-sec {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 4px;

        .icon {
          font-size: 16px;
          color: #4475b4;
          flex-shrink: 0;
          line-height: 0;
        }

        .name {
          color: #4475b4;
          font-size: 13px;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 0px;
        }

        .detail {
          color: #777;
          font-size: 12px;
          font-style: italic;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex-shrink: 0;
        }
      }

      .generate-arrow {
        display: flex;
        justify-content: center;
        align-items: center;
        background: none;
        border: none;
        padding: 4px;
        color: #999;
        font-size: 10px;
        flex-shrink: 0;
        transition: transform 0.2s;
        flex-shrink: 0;

        &:hover {
          color: #5b9bd5;
        }

        &.open {
          transform: rotate(180deg);
        }
      }

      .photo-actions {
        display: flex;
        align-items: center;

        .photo-btn {
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: 0.2s;

          &.upload {
            color: #4475b4;

            &:hover {
              background: #e8eff7;
            }
          }

          &.remove {
            color: #ff4444;

            &:hover {
              background: #fee;
            }
          }

          .icon {
            line-height: 0;
          }
        }
      }

      .remove-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        background: none;
        border: none;
        color: #999;
        padding: 4px;
        font-size: 18px;
        cursor: pointer;
        flex-shrink: 0;
        height: 24px;

        &:hover {
          color: #ff4444;
        }
      }
    }
  }

  .generate-btn {
    background: transparent;
    border: none;
    padding: 0px;
    margin-left: 5px;

    .icon {
      color: #4475b4;
      flex-shrink: 0;
      line-height: 0;
    }

    &.loading {
      animation: spin 1s linear infinite;
      top: 40%;
    }

    &:hover {
      color: #5b9bd5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .error-message {
    color: #ff4444;
    font-size: 12px;
  }

  .char-counter {
    text-align: right;
    font-size: 12px;
    color: #666;
    width: 100%;
  }

  .loader-icon {
    margin-right: 6px;

    &.loading {
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
}
</style>